'use client'

import { InstructionsSizeDrawer } from '@/app/[locale]/product/[slug]/_components/product-info-sizes/instruction-size-drawer'
import { Divider } from '@/components/ui/divider'
import { PRODUCT_SIZE } from '@/lib/constants/size'
import { cn } from '@/lib/utils'
import { __cProductService } from '@/services/client/__c.product.service'
import { ProductDetailFormatted } from '@/types/product-detail'
import { SizeComboResponse, SizeDataCategory } from '@/types/size'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'
import { useBoolean } from 'usehooks-ts'

type Props = {
  sizeChart?: string
  gender_type?: string
  size_guide?: SizeDataCategory
  productId?: string
  product: ProductDetailFormatted
  setIsSizeGuideDrawerOpen?: (open: boolean) => void
  className?: string
}

export const InstructionSize = ({
  sizeChart,
  gender_type,
  size_guide: initialSizeGuide,
  productId,
  product,
  setIsSizeGuideDrawerOpen,
  className,
}: Props) => {
  const t = useTranslations('product_detail.instruction_size')
  const drawerControl = useBoolean()
  const [activeTab, setActiveTab] = useState('size-table')
  const [comboResponse, setComboResponse] = useState<SizeComboResponse | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(false)

  // Ưu tiên data từ API (combo), fallback về props (sản phẩm thường)
  const effectiveSizeChart =
    PRODUCT_SIZE[comboResponse?.sizechart as keyof typeof PRODUCT_SIZE] ??
    sizeChart ??
    ''
  const effectiveGenderType = comboResponse?.gender_type ?? gender_type ?? ''
  const currentSizeGuide = comboResponse?.size_guide ?? initialSizeGuide ?? null

  const isBraOrLegging = useMemo(
    () => ['BRA', 'LEGGING'].includes(effectiveSizeChart),
    [effectiveSizeChart]
  )

  // Cấu hình các tuỳ chọn nút
  const buttonOptions = useMemo(() => {
    const options = [
      {
        label: isBraOrLegging ? t('size_table') : t('choose_size'),
        value: 'size-table',
      },
    ]

    if (isBraOrLegging) {
      options.push({
        label: t('choose_size'),
        value: 'guide',
      })
    }

    return options
  }, [isBraOrLegging, t])

  // Track drawer state to prevent URL updates when drawer is open
  useEffect(() => {
    setIsSizeGuideDrawerOpen?.(drawerControl.value)
  }, [drawerControl.value, setIsSizeGuideDrawerOpen])

  const openDrawer = async (tabValue: string) => {
    const isComboPart = productId && productId !== product.id
    if (isComboPart && !comboResponse) {
      setIsLoading(true)
      try {
        const data = await __cProductService.getProductSizeGuide(productId)
        if (data) {
          setComboResponse(data)
        }
      } catch (error) {
        console.error('Failed to fetch size guide:', error)
      } finally {
        setIsLoading(false)
      }
    }
    setActiveTab(tabValue)
    drawerControl.setTrue()
  }

  const handleDrawerCancel = () => {
    drawerControl.setFalse()
  }

  if (!product?.is_setdo && !effectiveSizeChart) {
    return null
  }

  return (
    <div className='flex items-center py-1'>
      {buttonOptions.map((option, index) => (
        <div key={option.value} className='flex items-center'>
          {index > 0 && <Divider className='mx-2 h-4.5' type='vertical' />}
          <button
            className={cn(
              'font-sans text-sm leading-4.5 text-primary underline',
              className,
              isLoading && 'pointer-events-none opacity-50'
            )}
            onClick={() => openDrawer(option.value)}
            ga-tracking-value={`product__size_guide`}
            ga-tracking-label={`product__size_guide`}
            disabled={isLoading}
          >
            {isLoading ? 'Đang tải...' : option.label}
          </button>
        </div>
      ))}

      {drawerControl.value && (
        <InstructionsSizeDrawer
          onCancel={handleDrawerCancel}
          tabDefault={activeTab}
          sizeChart={effectiveSizeChart}
          gender_type={effectiveGenderType}
          size_guide={currentSizeGuide as SizeDataCategory}
          productId={productId}
          product={product}
        />
      )}
    </div>
  )
}
